import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertTriangle, HelpCircle } from "lucide-react";

const ConfirmCtx = createContext({ confirm: async () => false, promptDialog: async () => null });

export function useConfirm() { return useContext(ConfirmCtx).confirm; }
export function usePromptDialog() { return useContext(ConfirmCtx).promptDialog; }

export default function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // {kind:'confirm'|'prompt', ...}
  const [promptValue, setPromptValue] = useState("");
  const resolver = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolver.current = resolve;
      setState({ kind: "confirm", title: opts.title || "Confirmar", description: opts.description || "",
        confirmText: opts.confirmText || "Confirmar", cancelText: opts.cancelText || "Cancelar",
        variant: opts.variant || "danger" });
    });
  }, []);

  const promptDialog = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolver.current = resolve;
      setPromptValue(opts.defaultValue || "");
      setState({ kind: "prompt", title: opts.title || "Ingresar valor", description: opts.description || "",
        placeholder: opts.placeholder || "", confirmText: opts.confirmText || "Guardar", cancelText: opts.cancelText || "Cancelar" });
    });
  }, []);

  const close = (value) => {
    const r = resolver.current;
    resolver.current = null;
    setState(null);
    setPromptValue("");
    if (r) r(value);
  };

  const confirmVariant = state?.variant === "danger"
    ? "bg-red-600 hover:bg-red-700 text-white"
    : state?.variant === "warning"
    ? "bg-amber-600 hover:bg-amber-700 text-white"
    : "bg-indigo-600 hover:bg-indigo-700 text-white";

  return (
    <ConfirmCtx.Provider value={{ confirm, promptDialog }}>
      {children}
      <Dialog open={!!state} onOpenChange={(v) => { if (!v) close(state?.kind === "prompt" ? null : false); }}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="platform-confirm">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-slate-900">
              {state?.variant === "danger" ? <AlertTriangle size={18} className="text-red-600" /> : <HelpCircle size={18} className="text-indigo-600" />}
              {state?.title}
            </DialogTitle>
            {state?.description && <DialogDescription className="text-slate-600">{state.description}</DialogDescription>}
          </DialogHeader>
          {state?.kind === "prompt" && (
            <input autoFocus data-testid="platform-prompt-input" value={promptValue}
              onChange={(e) => setPromptValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") close(promptValue); }}
              placeholder={state.placeholder}
              className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:border-indigo-400 outline-none" />
          )}
          <DialogFooter className="gap-2">
            <button data-testid="platform-confirm-cancel"
              onClick={() => close(state?.kind === "prompt" ? null : false)}
              className="h-10 px-4 rounded-lg text-sm font-medium border border-slate-200 bg-white hover:bg-slate-50">
              {state?.cancelText}
            </button>
            <button data-testid="platform-confirm-ok"
              onClick={() => close(state?.kind === "prompt" ? promptValue : true)}
              className={`h-10 px-4 rounded-lg text-sm font-semibold ${confirmVariant}`}>
              {state?.confirmText}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmCtx.Provider>
  );
}
